import { makeAutoObservable, runInAction } from 'mobx';
import {
  ViewMode,
  ViewModePreferences,
  UserPreferences,
  PreferencesStoreConfig,
} from './types';

const DEFAULT_PREFERENCES: UserPreferences = {
  theme: 'light',
  locale: 'en-US',
  dateFormat: 'DD-MM',
  timeFormat: 'H24',
  viewModes: {
    deviceList: 'card',
    groupDashboard: 'card',
  },
  groupDashboardDays: 7,
  timelineEventStyle: 'icons',
  timelineMagnifier: true,
  timelineTossAndTurns: false,
  timelineRowSize: 'S',
  timelineLabelWidthDevice: 72,
  timelineLabelWidthGroup: 120,
};

/**
 * User-scoped preferences store with optional backend sync
 *
 * Storage strategy:
 * 1. If userId available: localStorage key = `${namespace}:user:${userId}:preferences`
 * 2. If no userId (not logged in): localStorage key = `${namespace}:preferences`
 * 3. If apiEndpoint configured: sync with backend, localStorage as fallback
 */
export class PreferencesStore {
  private _preferences: UserPreferences = { ...DEFAULT_PREFERENCES };
  private _config: PreferencesStoreConfig;
  private _initialized = false;
  private _syncing = false;

  constructor(config: PreferencesStoreConfig) {
    this._config = config;
    makeAutoObservable(this, {}, { autoBind: true });
    this.loadFromLocalStorage();
  }

  // Storage key based on user context
  private getStorageKey(): string {
    const userId = this._config.getUserId?.();
    if (userId) {
      return `${this._config.namespace}:user:${userId}:preferences`;
    }
    return `${this._config.namespace}:preferences`;
  }

  // Load preferences from localStorage
  private loadFromLocalStorage() {
    try {
      const stored = localStorage.getItem(this.getStorageKey());
      if (stored) {
        const parsed = JSON.parse(stored);
        this._preferences = {
          ...DEFAULT_PREFERENCES,
          ...parsed,
          viewModes: { ...DEFAULT_PREFERENCES.viewModes, ...parsed.viewModes },
        };
      } else {
        // Nothing stored under this key: the state must reflect that, or the
        // previous user's values would linger after a switch.
        this._preferences = { ...DEFAULT_PREFERENCES };
      }
      this.applyTheme(this._preferences.theme === 'dark');
    } catch (e) {
      console.error('Failed to load preferences from localStorage:', e);
    }
  }

  /**
   * Re-read preferences under the key that belongs to the user who is signed in
   * now. Call it after login and after a session is restored.
   *
   * The constructor runs at module import, before any user id exists, so it can
   * only read the anonymous key. Saves made while signed in go to the user key.
   * Without this call the two keys never meet and every new session starts from
   * the defaults, with the saved values sitting unread on disk.
   */
  loadForCurrentUser() {
    const key = this.getStorageKey();
    const anonymousKey = `${this._config.namespace}:preferences`;
    try {
      if (key !== anonymousKey && localStorage.getItem(key) === null) {
        // First sign-in on this browser: carry the anonymous choices over
        // rather than resetting the user to the defaults.
        const anonymous = localStorage.getItem(anonymousKey);
        if (anonymous !== null) {
          localStorage.setItem(key, anonymous);
        }
      }
    } catch (e) {
      console.error('Failed to migrate preferences to the user key:', e);
    }
    this.loadFromLocalStorage();
  }

  // Save preferences to localStorage
  private saveToLocalStorage() {
    try {
      localStorage.setItem(
        this.getStorageKey(),
        JSON.stringify(this._preferences)
      );
    } catch (e) {
      console.error('Failed to save preferences to localStorage:', e);
    }
  }

  // Apply theme to document
  private applyTheme(isDark: boolean) {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    this._config.onDarkModeChange?.(isDark);
  }

  /**
   * Initialize store - call after login to sync with backend
   */
  async initialize(backendPreferences?: UserPreferences) {
    if (backendPreferences) {
      runInAction(() => {
        this._preferences = {
          ...DEFAULT_PREFERENCES,
          ...backendPreferences,
          viewModes: {
            ...DEFAULT_PREFERENCES.viewModes,
            ...backendPreferences.viewModes,
          },
        };
        this._initialized = true;
      });
      this.saveToLocalStorage();
      this.applyTheme(this._preferences.theme === 'dark');
      return;
    }

    if (this._config.apiEndpoint && this._config.getAuthToken?.()) {
      await this.syncFromBackend();
    } else {
      this.loadFromLocalStorage();
    }

    runInAction(() => {
      this._initialized = true;
    });
  }

  /**
   * Reset store on logout
   */
  reset() {
    runInAction(() => {
      this._preferences = { ...DEFAULT_PREFERENCES };
      this._initialized = false;
    });
    this.loadFromLocalStorage();
  }

  private async syncFromBackend(): Promise<void> {
    if (!this._config.apiEndpoint || !this._config.getAuthToken) return;

    const token = this._config.getAuthToken();
    if (!token) return;

    try {
      const baseUrl = this._config.apiBaseUrl || '';
      const response = await fetch(`${baseUrl}${this._config.apiEndpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accesstoken: token,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.preferences) {
          runInAction(() => {
            this._preferences = {
              ...DEFAULT_PREFERENCES,
              ...data.preferences,
              viewModes: {
                ...DEFAULT_PREFERENCES.viewModes,
                ...data.preferences.viewModes,
              },
            };
          });
          this.saveToLocalStorage();
          this.applyTheme(this._preferences.theme === 'dark');
        }
      }
    } catch (e) {
      console.error('Failed to sync preferences from backend:', e);
      this.loadFromLocalStorage();
    }
  }

  private async syncToBackend(): Promise<void> {
    if (
      !this._config.apiEndpoint ||
      !this._config.getAuthToken ||
      this._syncing
    )
      return;

    const token = this._config.getAuthToken();
    if (!token) return;

    this._syncing = true;
    try {
      const baseUrl = this._config.apiBaseUrl || '';
      await fetch(`${baseUrl}${this._config.apiEndpoint}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accesstoken: token,
        },
        body: JSON.stringify(this._preferences),
      });
    } catch (e) {
      console.error('Failed to sync preferences to backend:', e);
    } finally {
      this._syncing = false;
    }
  }

  private updatePreference<K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ) {
    runInAction(() => {
      this._preferences[key] = value;
    });
    this.saveToLocalStorage();
    this.syncToBackend();
  }

  // Getters
  get preferences(): UserPreferences {
    return this._preferences;
  }

  get initialized(): boolean {
    return this._initialized;
  }

  get theme(): 'light' | 'dark' {
    return this._preferences.theme || 'light';
  }

  get isDarkMode(): boolean {
    return this._preferences.theme === 'dark';
  }

  get locale(): string {
    return this._preferences.locale || 'en-US';
  }

  get dateFormat(): string {
    return this._preferences.dateFormat || 'DD-MM';
  }

  get timeFormat(): string {
    return this._preferences.timeFormat || 'H24';
  }

  get viewModes(): ViewModePreferences {
    return this._preferences.viewModes || DEFAULT_PREFERENCES.viewModes!;
  }

  get groupDashboardDays(): number {
    return this._preferences.groupDashboardDays || 7;
  }

  // Setters
  set theme(value: 'light' | 'dark') {
    this.applyTheme(value === 'dark');
    this.updatePreference('theme', value);
  }

  set isDarkMode(value: boolean) {
    this.theme = value ? 'dark' : 'light';
  }

  set locale(value: string) {
    this.updatePreference('locale', value);
  }

  set dateFormat(value: string) {
    this.updatePreference('dateFormat', value);
  }

  set timeFormat(value: string) {
    this.updatePreference('timeFormat', value);
  }

  set groupDashboardDays(value: number) {
    this.updatePreference('groupDashboardDays', value);
  }

  get timelineEventStyle(): 'dots' | 'icons' {
    return this._preferences.timelineEventStyle ?? 'icons';
  }

  set timelineEventStyle(value: 'dots' | 'icons') {
    this.updatePreference('timelineEventStyle', value);
  }

  get timelineMagnifier(): boolean {
    return this._preferences.timelineMagnifier ?? true;
  }

  set timelineMagnifier(value: boolean) {
    this.updatePreference('timelineMagnifier', value);
  }

  get timelineTossAndTurns(): boolean {
    return this._preferences.timelineTossAndTurns ?? false;
  }

  set timelineTossAndTurns(value: boolean) {
    this.updatePreference('timelineTossAndTurns', value);
  }

  get timelineTurnMarkers(): boolean {
    return this._preferences.timelineTurnMarkers ?? true;
  }

  set timelineTurnMarkers(value: boolean) {
    this.updatePreference('timelineTurnMarkers', value);
  }

  get timelineRowSize(): 'S' | 'M' | 'L' {
    return this._preferences.timelineRowSize ?? 'S';
  }

  set timelineRowSize(value: 'S' | 'M' | 'L') {
    this.updatePreference('timelineRowSize', value);
  }

  get timelineLabelWidthDevice(): number {
    return this._preferences.timelineLabelWidthDevice ?? 72;
  }

  set timelineLabelWidthDevice(value: number) {
    this.updatePreference('timelineLabelWidthDevice', value);
  }

  get timelineLabelWidthGroup(): number {
    return this._preferences.timelineLabelWidthGroup ?? 120;
  }

  set timelineLabelWidthGroup(value: number) {
    this.updatePreference('timelineLabelWidthGroup', value);
  }

  // Per-page viewMode methods
  getViewMode(page: keyof ViewModePreferences): ViewMode {
    return this._preferences.viewModes?.[page] || 'card';
  }

  setViewMode(page: keyof ViewModePreferences, value: ViewMode) {
    runInAction(() => {
      if (!this._preferences.viewModes) {
        this._preferences.viewModes = {};
      }
      this._preferences.viewModes[page] = value;
    });
    this.saveToLocalStorage();
    this.syncToBackend();
  }

  toggleViewMode(page: keyof ViewModePreferences) {
    const current = this.getViewMode(page);
    this.setViewMode(page, current === 'list' ? 'card' : 'list');
  }

  // Methods
  toggleDarkMode() {
    this.isDarkMode = !this.isDarkMode;
  }

  /**
   * Handle OS dark mode preference change event
   */
  changeTheme(e: MediaQueryListEvent) {
    this.isDarkMode = e.matches;
  }

  updateAll(prefs: Partial<UserPreferences>) {
    runInAction(() => {
      if (prefs.viewModes) {
        this._preferences.viewModes = {
          ...this._preferences.viewModes,
          ...prefs.viewModes,
        };
        delete prefs.viewModes;
      }
      Object.assign(this._preferences, prefs);
    });
    if (prefs.theme !== undefined) {
      this.applyTheme(prefs.theme === 'dark');
    }
    this.saveToLocalStorage();
    this.syncToBackend();
  }
}

/**
 * Factory function to create a preferences store instance
 */
export function createPreferencesStore(
  config: PreferencesStoreConfig
): PreferencesStore {
  return new PreferencesStore(config);
}
